import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState
} from 'react'

import useIsomorphicLayoutEffect from '@vio/core/src/utils/react/hooks/useIsomorphicLayoutEffect'

interface BlockingSurface {
  owner?: string
}

type BlockingSurfaces = ReadonlySet<BlockingSurface>

type Register = (owner?: string) => () => void

const RegisterContext = createContext<Register | undefined>(undefined)

const SurfacesContext = createContext<BlockingSurfaces | undefined>(undefined)

export const BlockingSurfacesProvider = ({children}: {children: ReactNode}) => {
  const [surfaces, setSurfaces] = useState<BlockingSurfaces>(() => new Set())

  const register = useCallback((owner?: string) => {
    const surface: BlockingSurface = {owner}

    setSurfaces(open => new Set(open).add(surface))

    return () =>
      setSurfaces(open => {
        const without = new Set(open)
        without.delete(surface)

        return without
      })
  }, [])

  return (
    <RegisterContext.Provider value={register}>
      <SurfacesContext.Provider value={surfaces}>
        {children}
      </SurfacesContext.Provider>
    </RegisterContext.Provider>
  )
}

export const useRegisterBlockingSurface = (
  isOpen: boolean,
  surfaceId?: string
) => {
  const register = useContext(RegisterContext)

  useIsomorphicLayoutEffect(() => {
    if (isOpen && register) return register(surfaceId)
  }, [isOpen, register, surfaceId])
}

export const useIsBlockingSurfaceOpen = (except?: string): boolean => {
  const surfaces = useContext(SurfacesContext)

  if (!surfaces) {
    throw new Error(
      'Reading blocking surfaces requires a BlockingSurfacesProvider'
    )
  }

  if (except === undefined) return surfaces.size > 0

  return Array.from(surfaces).some(surface => surface.owner !== except)
}
